import moment from "moment";

import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";

const TICKS_PER_DAY = 864000000000; // 100 nanoseconds per tick * 60 seconds per minute * 60 minutes per hour * 24 hours per day

class DateHelper {
    /**
     * @deprecated use formatDateLuxon instead
     * Format a date value into a string using moment library.
     * @param date The date value to format.
     * @param format The format string in Moment.js format (like "YYYY-MM-DD").
     * @returns Returns the formatted string if the date is valid, else null.
     */
    public static formatDate(date: string | Date, format: string): string | null {
        const parsed = date && moment(date);

        return parsed?.isValid()
            ? parsed.format(format)
            : null;
    }

    /**
     * Format a date value into a string using luxon library.
     * @param date The date value to format.
     * @param format The format string in luxon format (like "yyyy-MM-dd").
     * @returns Returns the formatted string if the date is valid, else null.
     */
    public static formatDateLuxon(date: string | Date, format: string): string | null {
        const parsed = LuxonDateTimeParser.parse(date);

        return parsed?.toFormat(format);
    }

    /**
     * Format a date value into a string using the standard ISO 8601 date-and-time format (i.e. yyyy-mm-dd'T'HH:mm:ss.SSS'Z').
     * @param date The date value to format.
     * @returns Returns the formatted string if the date is valid, else null.
     */
    public static formatIsoFull(date: string | Date): string | null {
        const parsed = date && moment(date);

        return parsed?.isValid()
            ? parsed?.toISOString()
            : null;
    }

    /**
     * Format a date value into a string using the standard ISO 8601 date-only format (i.e. yyyy-mm-dd).
     * @param date The date value to format.
     * @returns Returns the formatted string if the date is valid, else null.
     */
    public static formatIsoDateOnly(date: string | Date): string | null {
        return DateHelper.formatDate(date, "YYYY-MM-DD");
    }

    public static isValidDate(date: string | Date, minDate: string | Date = null, maxDate: string | Date = null, allowNullOrUndefined: boolean = false): boolean {
        if (!date)
            return allowNullOrUndefined;

        return moment(date).isValid()
            && this.isInRange(date, minDate, maxDate);
    }

    public static isInRange(date: string | Date, minDate: string | Date = null, maxDate: string | Date = null): boolean {
        const parsedDate = moment(date);
        return (!maxDate || parsedDate.isBefore(maxDate))
            && (!minDate || parsedDate.isAfter(minDate));
    }

    public static isInRangeOrSameDay(date: string | Date, minDate: string | Date = null, maxDate: string | Date = null): boolean {
        const parsedDate = moment(date);
        return (!maxDate || parsedDate.isSameOrBefore(maxDate))
            && (!minDate || parsedDate.isSameOrAfter(minDate));
    }

    public static parseOptional(date: Date | string): ReturnType<typeof moment> | null {
        return date
            ? moment(date)
            : null;
    }

    /**
     * Format a date value as the UTC date format
     * @param {Date} date
     * @param {boolean} startOfDay
     * @returns {Date}
     */
    public static createDateAsUTC(date: Date, startOfDay: boolean = false): Date {
        if (!date)
            return null;

        if (!(date instanceof Date))
            date = LuxonDateTimeParser.parse(date)?.toJSDate();

        if (startOfDay)
            return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0));
        else
            return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
    }

    /**
     * Convert date ticks to an equivalent number of days, rounded to the nearest day.
     * @param ticks The number of date ticks.
     */
    public static TicksToDays(ticks: number): number {
        const days = ticks / TICKS_PER_DAY;
        return Math.round(days);
    }
}

export { DateHelper };
